import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import axios from '../utils/axios';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { LoadingPage } from '../components/feedback/Spinner';
import Layout from '../components/layout/Layout';

interface ProfileData {
  id: number;
  email: string;
  full_name: string;
  credits: number;
  email_verified: boolean;
  created_at: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Profile form
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/api/profile');
      setProfile(response.data);
      setFullName(response.data.full_name || '');
      setEmail(response.data.email || '');
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      setErrorMessage('Failed to load profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await axios.put('/api/profile', {
        full_name: fullName,
        email: email
      });
      
      setProfile(response.data.user);
      setSuccessMessage('Profile updated successfully!');
      
      // If email changed, show verification message
      if (email !== profile?.email) {
        setSuccessMessage('Profile updated! Please verify your new email address.');
      }
    } catch (error: any) {
      setErrorMessage(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSuccess('');
    setPasswordError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    setIsChangingPassword(true);

    try {
      await axios.put('/api/profile/password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      setPasswordSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordError(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (isLoading) {
    return <LoadingPage message="Loading profile..." />;
  }

  return (
    <Layout>
      <div className="profile-page">
        <header className="profile-header">
          <div className="profile-header-content">
            <div>
              <h1 className="profile-title">Profile Settings</h1>
              <p className="profile-subtitle">Manage your account information and preferences</p>
            </div>
            
            <div className="credits-display">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              <span>{profile?.credits || 0} Credits</span>
            </div>
          </div>
        </header>

        <main className="profile-content">
          <div className="profile-container">
            {/* Profile Information Card */}
            <Card className="profile-card">
              <CardBody>
                <h2 className="profile-section-title">Personal Information</h2>
                
                <form onSubmit={handleUpdateProfile} className="profile-form">
                  <div className="form-group">
                    <Input
                      label="Full Name"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <Input
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                    {profile && !profile.email_verified && (
                      <p className="input-hint warning">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        Email not verified
                      </p>
                    )}
                  </div>

                  <div className="profile-info-grid">
                    <div className="profile-info-item">
                      <span className="profile-info-label">Account Created</span>
                      <span className="profile-info-value">
                        {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div className="profile-info-item">
                      <span className="profile-info-label">Total Credits</span>
                      <span className="profile-info-value">{profile?.credits || 0}</span>
                    </div>
                  </div>

                  {successMessage && <Alert variant="success">{successMessage}</Alert>}
                  {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={isSaving}
                  >
                    Save Changes
                  </Button>
                </form>
              </CardBody>
            </Card>

            {/* Password Change Card */}
            <Card className="profile-card">
              <CardBody>
                <h2 className="profile-section-title">Change Password</h2>
                
                <form onSubmit={handleChangePassword} className="profile-form">
                  <div className="form-group">
                    <Input
                      label="Current Password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <Input
                      label="New Password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 8 characters)"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <Input
                      label="Confirm New Password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                    />
                  </div>

                  {passwordSuccess && <Alert variant="success">{passwordSuccess}</Alert>}
                  {passwordError && <Alert variant="error">{passwordError}</Alert>}

                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={isChangingPassword}
                  >
                    Update Password
                  </Button>
                </form>
              </CardBody>
            </Card>

            {/* Account Actions Card */}
            <Card className="profile-card">
              <CardBody>
                <h2 className="profile-section-title">Account Actions</h2>
                
                <div className="account-actions">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => navigate('/pricing')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="1" x2="12" y2="23" />
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H17" />
                    </svg>
                    Purchase Credits
                  </Button>
                  
                  <Button
                    variant="ghost"
                    fullWidth
                    onClick={() => {
                      if (window.confirm('Are you sure you want to sign out?')) {
                        logout();
                        navigate('/login');
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Sign Out
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default Profile;
